import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { MAX_PAGE_SIZE } from './apiConstants';

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSourceFreshness = graphql(/* GraphQL */ `
  query SourceFreshness(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $sourceNames: [String!]!
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        sources(first: $first, after: $after, filter: { sourceNames: $sourceNames }) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              sourceName
              freshness {
                freshnessStatus
                snapshottedAt
              }
            }
          }
        }
      }
    }
  }
`);

type SourceByEnvironmentParams = {
  environmentId: number | undefined;
  userId: number | 'loading' | null;
  after?: string;
  sourceNames: string[];
  pause?: boolean;
};

export const useSourceFreshness = ({
  environmentId = 0,
  userId,
  sourceNames,
  after,
  pause,
}: SourceByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetSourceFreshness,
      variables: {
        environmentId,
        userId: userId as number | null,
        sourceNames,
        after,
        first: MAX_PAGE_SIZE,
      },
      pause: pause || !environmentId || !sourceNames.length || userId === 'loading',
    },
    (data) => {
      const sources = data.environment.applied?.sources;
      const pageInfo = sources?.pageInfo;
      if (sources == null || pageInfo == null) return undefined;
      return {
        totalCount: sources.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.applied?.sources.edges.map((edge) => edge.node);
    },
    (data) => {
      return data.environment.applied?.lastUpdatedAt;
    },
  );

  useCacheMonitor({
    applied: query.lastUpdatedAt,
  });

  return query;
};
